import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChannelStory, SeeNumbersHint } from "./channels/ChannelStory";
import { Mail, Smartphone, DollarSign, ShoppingCart, TrendingUp, Send, Eye, MousePointerClick, Ticket, ChevronDown } from 'lucide-react';

const CHANNEL_CONFIG = {
  email: { label: 'Email', color: 'emerald', icon: Mail, bg: 'from-emerald-500/10 to-green-500/10', border: 'border-emerald-500/20' },
  push: { label: 'Push Notification', color: 'violet', icon: Smartphone, bg: 'from-violet-500/10 to-purple-500/10', border: 'border-violet-500/20' },
  sms: { label: 'SMS / Text', color: 'cyan', icon: Send, bg: 'from-cyan-500/10 to-blue-500/10', border: 'border-cyan-500/20' },
};

function formatMoney(val) {
  if (!val && val !== 0) return '$0';
  return '$' + val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatNum(val) {
  if (!val && val !== 0) return '0';
  return val.toLocaleString();
}

export function AppFrontSection({ appfrontData, story = null }) {
  const campaigns = appfrontData?.campaigns || [];
  const summary = appfrontData?.summary;
  const [isOpen, setIsOpen] = useState(false);

  const hasSales = useMemo(() => {
    return campaigns.some(c => c.total_generated_sales && c.total_generated_sales > 0);
  }, [campaigns]);

  // Group campaigns by month (report_start is already first-of-month per
  // the backend parser, so it's a clean grouping key). Each month gets a
  // per-month summary so the collapsed row can show meaningful detail.
  const monthGroups = useMemo(() => {
    const map = new Map();
    for (const c of campaigns) {
      const key = (c.report_start || c.date || '').slice(0, 7); // YYYY-MM
      if (!key) continue;
      if (!map.has(key)) map.set(key, { key, campaigns: [] });
      map.get(key).campaigns.push(c);
    }
    const groups = Array.from(map.values()).sort((a, b) => b.key.localeCompare(a.key)); // newest first
    return groups.map((g) => {
      const totalSales = g.campaigns.reduce((s, c) => s + (c.total_generated_sales || 0), 0);
      const totalRedeemed = g.campaigns.reduce((s, c) => s + (c.redeemed || 0), 0);
      const totalEmailDelivered = g.campaigns.reduce((s, c) => s + (c.email_success || 0), 0);
      const totalEmailOpened = g.campaigns.reduce((s, c) => s + (c.email_opened || 0), 0);
      const totalPushDelivered = g.campaigns.reduce((s, c) => s + (c.push_success || 0), 0);
      const totalPushSeen = g.campaigns.reduce((s, c) => s + (c.push_seen || 0), 0);
      const emailOpenRate = totalEmailDelivered > 0 ? (totalEmailOpened / totalEmailDelivered) * 100 : 0;
      const pushSeenRate = totalPushDelivered > 0 ? (totalPushSeen / totalPushDelivered) * 100 : 0;
      const [yy, mm] = g.key.split('-');
      const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const label = mm ? `${monthNames[parseInt(mm,10)-1]} ${yy}` : g.key;
      return {
        ...g,
        label,
        totalSales,
        totalRedeemed,
        totalEmailDelivered,
        totalEmailOpened,
        totalPushDelivered,
        totalPushSeen,
        emailOpenRate,
        pushSeenRate,
      };
    });
  }, [campaigns]);

  // Per-month open/closed state. Most recent month defaults open, the
  // rest stay collapsed so the page doesn't unfurl into a wall of data.
  const [monthOpenMap, setMonthOpenMap] = useState({});
  const isMonthOpen = (key, idx) => {
    if (key in monthOpenMap) return monthOpenMap[key];
    return idx === 0; // newest month opens by default
  };
  const toggleMonth = (key, idx) => setMonthOpenMap((m) => ({ ...m, [key]: !isMonthOpen(key, idx) }));

  if (!summary || campaigns.length === 0) return null;

  return (
    <div className="relative z-10 mb-8" data-testid="appfront-section">
      <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl p-6 border border-green-500/20">
        {/* Header (clickable toggle) */}
        <div
          onClick={() => setIsOpen(v => !v)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsOpen(v => !v); } }}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          data-testid="appfront-toggle"
          className="flex items-center gap-3 cursor-pointer select-none group"
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-green-400 text-xs font-semibold uppercase tracking-wider">App Front</p>
            <p className="text-white text-lg font-bold">App Engagement & Loyalty</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full border border-green-500/30">
              {summary.total_campaigns} campaign{summary.total_campaigns !== 1 ? 's' : ''}
            </span>
            <ChevronDown className={`w-5 h-5 text-green-300 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>

        <div onClick={() => setIsOpen(v => !v)} className="cursor-pointer select-none group" data-testid="appfront-story-area">
          <ChannelStory story={story} testId="appfront-section-story" />
          <SeeNumbersHint open={isOpen} className="text-green-300" />
        </div>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key="appfront-body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="pt-6">

        {/* Revenue Attribution Hero (if available) */}
        {hasSales && (
          <div className="bg-gradient-to-r from-amber-500/15 to-green-500/15 rounded-xl p-5 border border-amber-500/30 mb-6" data-testid="appfront-revenue">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-5 h-5 text-amber-400" />
              <p className="text-white font-semibold">Revenue Generated from App Campaigns</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-3xl font-bold text-amber-400">{formatMoney(summary.total_sales)}</p>
                <p className="text-slate-500 text-xs mt-1">Total Tracked Sales</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-green-400">{formatNum(summary.total_redeemed)}</p>
                <p className="text-slate-500 text-xs mt-1">Coupons Redeemed</p>
              </div>
              {summary.avg_purchase_value > 0 && (
                <div>
                  <p className="text-3xl font-bold text-emerald-400">{formatMoney(summary.avg_purchase_value)}</p>
                  <p className="text-slate-500 text-xs mt-1">Avg Purchase Value</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Channel Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {summary.total_email_delivered > 0 && (
            <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
              <Mail className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
              <p className="text-white text-lg font-bold">{formatNum(summary.total_email_delivered)}</p>
              <p className="text-slate-500 text-xs">Emails Delivered</p>
              {summary.email_open_rate > 0 && (
                <p className={`text-sm font-medium mt-1 ${summary.email_open_rate >= 20 ? 'text-emerald-400' : 'text-slate-400'}`}>
                  {summary.email_open_rate}% open rate
                </p>
              )}
            </div>
          )}
          {summary.total_push_delivered > 0 && (
            <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
              <Smartphone className="w-5 h-5 text-violet-400 mx-auto mb-2" />
              <p className="text-white text-lg font-bold">{formatNum(summary.total_push_delivered)}</p>
              <p className="text-slate-500 text-xs">Push Delivered</p>
              {summary.push_seen_rate > 0 && (
                <p className="text-sm font-medium mt-1 text-violet-400">
                  {summary.push_seen_rate}% seen
                </p>
              )}
            </div>
          )}
          {summary.total_email_clicked > 0 && (
            <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
              <MousePointerClick className="w-5 h-5 text-cyan-400 mx-auto mb-2" />
              <p className="text-white text-lg font-bold">{formatNum(summary.total_email_clicked)}</p>
              <p className="text-slate-500 text-xs">Email Clicks</p>
            </div>
          )}
          {/* App Audience tile — only meaningful when we actually captured
              per-campaign audience counts (rich CSV format). Hidden for
              the leaner "campaign stats only" layout where this is 0
              (the Revenue Hero above already tells that story). */}
          {summary.app_audience > 0 && (
            <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
              <TrendingUp className="w-5 h-5 text-amber-400 mx-auto mb-2" />
              <p className="text-white text-lg font-bold">{formatNum(summary.app_audience)}</p>
              <p className="text-slate-500 text-xs">App Audience Reached</p>
            </div>
          )}
        </div>

        {/* Individual Campaign Breakdown — grouped by month so the
            client can drill into one period at a time instead of
            scrolling through every campaign at once. Most recent month
            defaults open; older months stay collapsed. */}
        <div className="space-y-2">
          {monthGroups.map((g, idx) => {
            const open = isMonthOpen(g.key, idx);
            return (
              <div key={g.key} className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                {/* Month header (clickable) */}
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={() => toggleMonth(g.key, idx)}
                  data-testid={`appfront-month-toggle-${g.key}`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleMonth(g.key, idx); } }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`} />
                    <p className="text-white font-semibold text-sm whitespace-nowrap">{g.label}</p>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[11px] text-slate-400 tabular-nums">
                    <span>{g.campaigns.length} campaign{g.campaigns.length !== 1 ? 's' : ''}</span>
                    {g.totalRedeemed > 0 && (
                      <span className="text-amber-400">· {formatNum(g.totalRedeemed)} redeemed</span>
                    )}
                    {g.totalSales > 0 && (
                      <span className="text-green-400 font-bold">· {formatMoney(g.totalSales)} sales</span>
                    )}
                  </div>
                </div>

                {open && (
                  <div className="px-4 pb-4 space-y-3 border-t border-white/10 pt-3">
                    {/* Per-month summary chips — only show what's available */}
                    {(g.totalEmailDelivered > 0 || g.totalPushDelivered > 0) && (
                      <div className="flex flex-wrap gap-2 text-[11px]">
                        {g.totalEmailDelivered > 0 && (
                          <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">
                            {formatNum(g.totalEmailDelivered)} emails delivered · {g.emailOpenRate.toFixed(0)}% open
                          </span>
                        )}
                        {g.totalPushDelivered > 0 && (
                          <span className="px-2.5 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300">
                            {formatNum(g.totalPushDelivered)} pushes delivered · {g.pushSeenRate.toFixed(0)}% seen
                          </span>
                        )}
                      </div>
                    )}

                    {/* Individual campaign cards for this month */}
                    {g.campaigns.map((campaign, ci) => {
                      const channelConf = CHANNEL_CONFIG[campaign.channel] || CHANNEL_CONFIG.push;
                      const ChannelIcon = channelConf.icon;
                      const hasCoupon = campaign.campaign_type?.toLowerCase().includes('coupon');
                      const hasRevenue = campaign.total_generated_sales && campaign.total_generated_sales > 0;
                      return (
                        <div key={campaign.id || ci} className={`bg-gradient-to-r ${channelConf.bg} rounded-xl p-4 border ${channelConf.border}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className={`w-9 h-9 rounded-lg bg-${channelConf.color}-500/20 flex items-center justify-center shrink-0`}>
                                <ChannelIcon className={`w-4 h-4 text-${channelConf.color}-400`} />
                              </div>
                              <div className="min-w-0">
                                <p className="text-white text-sm font-medium truncate" title={campaign.campaign_name}>
                                  {campaign.campaign_name?.replace(/^\d{1,2}\/\d{1,2}\/\d{4}:\s*/, '') || 'Campaign'}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className={`text-xs text-${channelConf.color}-400 font-medium`}>{channelConf.label}</span>
                                  <span className="text-slate-600 text-xs">|</span>
                                  <span className="text-slate-500 text-xs">{campaign.date}</span>
                                  {hasCoupon && (
                                    <>
                                      <span className="text-slate-600 text-xs">|</span>
                                      <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
                                        <Ticket className="w-3 h-3" /> Coupon
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right shrink-0 ml-3">
                              <p className="text-slate-400 text-xs">
                                {(campaign.sent_to_groups || campaign.sent_coupon_to) ? "Sent to" : "Coupon seen"}
                              </p>
                              <p className="text-white text-sm font-bold">{formatNum(campaign.sent_to_groups || campaign.sent_coupon_to || campaign.push_seen || 0)}</p>
                            </div>
                          </div>

                          {/* Stats row */}
                          <div className="flex flex-wrap gap-3 text-xs">
                            {campaign.email_success && (
                              <>
                                <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-3 py-1.5">
                                  <Send className="w-3 h-3 text-emerald-400" />
                                  <span className="text-slate-400">Delivered:</span>
                                  <span className="text-white font-medium">{formatNum(campaign.email_success)}</span>
                                </div>
                                {campaign.email_opened && (
                                  <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-3 py-1.5">
                                    <Eye className="w-3 h-3 text-emerald-400" />
                                    <span className="text-slate-400">Opened:</span>
                                    <span className="text-white font-medium">{formatNum(campaign.email_opened)}</span>
                                    <span className="text-emerald-400 font-medium">
                                      ({(campaign.email_opened / campaign.email_success * 100).toFixed(1)}%)
                                    </span>
                                  </div>
                                )}
                                {campaign.email_clicked > 0 && (
                                  <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-3 py-1.5">
                                    <MousePointerClick className="w-3 h-3 text-cyan-400" />
                                    <span className="text-slate-400">Clicked:</span>
                                    <span className="text-white font-medium">{formatNum(campaign.email_clicked)}</span>
                                  </div>
                                )}
                              </>
                            )}
                            {campaign.push_success && (
                              <>
                                <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-3 py-1.5">
                                  <Smartphone className="w-3 h-3 text-violet-400" />
                                  <span className="text-slate-400">Delivered:</span>
                                  <span className="text-white font-medium">{formatNum(campaign.push_success)}</span>
                                </div>
                                {campaign.push_seen && (
                                  <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-3 py-1.5">
                                    <Eye className="w-3 h-3 text-violet-400" />
                                    <span className="text-slate-400">Seen:</span>
                                    <span className="text-white font-medium">{formatNum(campaign.push_seen)}</span>
                                  </div>
                                )}
                              </>
                            )}
                            {campaign.redeemed > 0 && (
                              <div className="flex items-center gap-1.5 bg-amber-500/10 rounded-lg px-3 py-1.5 border border-amber-500/20">
                                <ShoppingCart className="w-3 h-3 text-amber-400" />
                                <span className="text-amber-300">{formatNum(campaign.redeemed)} redeemed</span>
                                {campaign.redemption_rate > 0 && (
                                  <span className="text-amber-400 font-bold">({campaign.redemption_rate}%)</span>
                                )}
                              </div>
                            )}
                            {hasRevenue && (
                              <div className="flex items-center gap-1.5 bg-green-500/10 rounded-lg px-3 py-1.5 border border-green-500/20">
                                <DollarSign className="w-3 h-3 text-green-400" />
                                <span className="text-green-300 font-bold">{formatMoney(campaign.total_generated_sales)}</span>
                                {campaign.avg_purchase_value > 0 && (
                                  <span className="text-green-400/70 text-xs">({formatMoney(campaign.avg_purchase_value)} avg)</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Insight callout */}
        <div className="mt-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl p-4 border border-green-500/20">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
            <p className="text-green-300 text-sm">
              {hasSales ? (
                <>Your app campaigns generated <span className="text-white font-semibold">{formatMoney(summary.total_sales)} in tracked sales</span> from <span className="text-white font-semibold">{formatNum(summary.total_redeemed)} coupon redemptions</span>: proving direct revenue impact from customer engagement.</>
              ) : summary.email_open_rate > 0 ? (
                <>With a <span className="text-white font-semibold">{summary.email_open_rate}% email open rate</span>, your app audience is actively engaged: well above the industry average of 15-25% for restaurants.</>
              ) : (
                <>Your app is reaching <span className="text-white font-semibold">{formatNum(summary.app_audience)} customers</span> directly through push notifications: building a loyal, engaged audience.</>
              )}
            </p>
          </div>
        </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
